import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useRouteMatch } from 'react-router-dom'

import {
  BenefitDetailsResponse,
  NotifyEmployeesFormValues,
  ProductIds,
  RenewalPlanPeriodResponse,
  getBenefitDetails,
  getRenewalPlanPeriod,
  inviteEmployees,
  notifyEmployees,
} from 'core'
import benefitPlanPeriods, { PlanPeriodDto } from 'core/application/adminDashboard/benefitDetails/benefitPlanPeriods'
import getProductClassDetails from 'core/application/adminDashboard/benefitDetails/getProductClassDetails'
import { postBenefitRedesignDecision } from 'core/application/adminDashboard/benefitRenewal/benefitRedesign/benefitRedesign'
import AllowanceType from 'core/domain/benefit/allowanceType'
import { BenefitDesignStatus } from 'core/domain/benefit/benefitDesignStatus'

import useQueryParams from 'common/hooks/useQueryParams'
import useQueryRequest from 'common/hooks/useQueryRequest'

import { Alert, BenefitPlanPeriodDropDown, Button, Card, Col, Row, Skeleton } from 'common/components'
import { parseZoneFormat } from 'common/dateFormatters'
import { useCommandRequest } from 'common/hooks'

import ClassesSummary from '../../../../../common/components/benefit/ClassesSummary'
import { DESIGN_BENEFIT_PATH } from '../../../design'
import { BenefitDesignStep } from '../../../design/groupHra/DesignStepsMapping'
import SummaryEmpty from '../../SummaryEmpty'
import SummaryTitle from '../../SummaryTitle'
import RenewalDesignInfoBox from '../../common/RenewalDesignInfoBox'
import UpdateNotificationDateModal from '../../common/UpdateNotificationDateModal'

const GroupHraDesignBenefitSummary: React.FC = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const match = useRouteMatch()
  const queryParams = useQueryParams()

  const [selectedPlanPeriod, setSelectedPlanPeriod] = useState<PlanPeriodDto | null>()
  const [showNotificationDateEdit, setShowNotificationDateEdit] = useState(false)
  const benefitDesignFullPath = `${match.path}/${DESIGN_BENEFIT_PATH}/`
  const [planPeriods] = useQueryRequest<PlanPeriodDto[]>(null, () => benefitPlanPeriods())
  const [renewalPlanPeriod] = useQueryRequest<RenewalPlanPeriodResponse>(null, () => getRenewalPlanPeriod())
  const [planPeriodDetails, isPlanPeriodDetailsLoading, sendGetPlanPeriodDetails] =
    useQueryRequest<BenefitDetailsResponse>(null)
  const [sendCommand, isCommandLoading] = useCommandRequest()

  const planPeriodIdQueryParam = queryParams.get('planPeriodId')

  useEffect(() => {
    if (planPeriods && planPeriods.length > 0) {
      const found = planPeriodIdQueryParam
        ? planPeriods.find((x) => x.id === planPeriodIdQueryParam)
        : planPeriods.find((x) => x.active == true)

      if (found) {
        setSelectedPlanPeriod(found)
      }
    }
  }, [planPeriods])

  useEffect(() => {
    if (selectedPlanPeriod) {
      ;(async () => {
        await sendGetPlanPeriodDetails(() => getBenefitDetails(selectedPlanPeriod.id))
      })()
    }
  }, [selectedPlanPeriod])

  const handleNotificationModalSubmit = async (values: NotifyEmployeesFormValues) => {
    if (selectedPlanPeriod?.id === renewalPlanPeriod?.id) await sendCommand(() => notifyEmployees(values))
    else await sendCommand(() => inviteEmployees(values))
    await sendGetPlanPeriodDetails(() => getBenefitDetails(selectedPlanPeriod.id))
  }

  const handleBenefitStartDataEdit = () => {
    history.push(`${benefitDesignFullPath}${selectedPlanPeriod.id}/${BenefitDesignStep.BenefitStartDate}`)
  }

  const handleEligibleEmployeesEdit = () => {
    history.push(`${benefitDesignFullPath}${selectedPlanPeriod.id}/${BenefitDesignStep.ClassSelection}`)
  }

  const handleNotificationDateEdit = () => {
    setShowNotificationDateEdit(true)
  }

  const handleBenefitDesignEdit = () => {
    history.push(`${benefitDesignFullPath}${selectedPlanPeriod.id}/${BenefitDesignStep.DesignClasses}`)
  }

  const onPlanPeriodChanged = (planPeriodId: string) => {
    setSelectedPlanPeriod(planPeriods.find((x) => x.id === planPeriodId))
  }

  return (
    <Card
      title={
        <Row justify={'space-between'} className={'w-100'}>
          <Col>
            <SummaryTitle />
          </Col>
          <Col>
            {planPeriods && (
              <BenefitPlanPeriodDropDown
                defaultValue={planPeriodIdQueryParam}
                planPeriods={planPeriods}
                onPlanPeriodSelected={onPlanPeriodChanged}
                showStartDateAndEndDate={true}
              />
            )}
          </Col>
        </Row>
      }
      className="benefit_details__summary"
    >
      <Skeleton active loading={isPlanPeriodDetailsLoading} paragraph={{ rows: 10 }}>
        {planPeriodDetails && (
          <>
            {planPeriodDetails.benefitDesignStatus === BenefitDesignStatus.Completed ||
            planPeriodDetails.benefitDesignStatus === BenefitDesignStatus.Signed ? (
              <>
                {renewalPlanPeriod && (
                  <RenewalDesignInfoBox
                    renewalPlanPeriod={renewalPlanPeriod}
                    renewalDecisionHandler={(useSame) => postBenefitRedesignDecision(useSame)}
                  />
                )}

                <Row className={'mb-8'}>
                  <Col span={24}>
                    <Row className="with_card">
                      <Col span={18} className="content">
                        <h5 className="mb-0">
                          <b>{t('admin-dashboard.benefit-details.summary.start-date-title')}</b>
                        </h5>
                        <h5 className="mb-0">{parseZoneFormat(planPeriodDetails.startDate)}</h5>
                      </Col>
                      <Col span={6} className="flex justify-end">
                        <Button
                          type="link"
                          className={'t-start-date'}
                          disabled={planPeriodDetails?.benefitDesignStatus === BenefitDesignStatus.Signed}
                          onClick={handleBenefitStartDataEdit}
                        >
                          {t('admin-dashboard.benefit-details.summary.edit-button')}
                        </Button>
                      </Col>
                    </Row>
                  </Col>
                </Row>

                <Row className={'mb-8'}>
                  <Col span={24}>
                    <Row className="with_card">
                      <Col span={18} className="content">
                        <h5 className="mb-0">
                          <h5>
                            <b>{t('admin-dashboard.benefit-details.summary.class-selection-title')}</b>
                          </h5>
                        </h5>
                        <h5 className="mb-0">
                          {selectedPlanPeriod.sameClassForAll
                            ? t('admin-dashboard.benefit-details.summary.same-class-msg')
                            : t('admin-dashboard.benefit-details.summary.different-class-msg')}
                        </h5>
                      </Col>
                      <Col span={6} className="flex justify-end">
                        <Button
                          type="link"
                          className={'t-start-date'}
                          disabled={planPeriodDetails?.benefitDesignStatus === BenefitDesignStatus.Signed}
                          onClick={() => {
                            history.push(
                              `${benefitDesignFullPath}${selectedPlanPeriod.id}/${BenefitDesignStep.ClassSelection}`,
                            )
                          }}
                        >
                          {t('admin-dashboard.benefit-details.summary.edit-button')}
                        </Button>
                      </Col>
                    </Row>
                  </Col>
                </Row>

                <Row className={'mb-8'}>
                  <Col span={24}>
                    <Row className="with_card">
                      <Col span={18} className="content">
                        <h5 className="mb-0">
                          <b>{t('admin-dashboard.benefit-details.summary.allowance-type-title')}</b>
                        </h5>
                        <h5 className="mb-0">
                          {selectedPlanPeriod.allowanceType === AllowanceType.Annual
                            ? t('admin-dashboard.benefit-details.summary.allowance-full-type')
                            : t('admin-dashboard.benefit-details.summary.allowance-monthly-type')}
                        </h5>
                      </Col>

                      <Col span={6} className="flex justify-end">
                        <Button
                          type="link"
                          className={'t-eligible-employees'}
                          disabled={!planPeriodDetails.isEditable}
                          onClick={() => {
                            history.push(
                              `${benefitDesignFullPath}${selectedPlanPeriod.id}/${BenefitDesignStep.AllowanceType}`,
                            )
                          }}
                        >
                          {t('admin-dashboard.benefit-details.summary.edit-button')}
                        </Button>
                      </Col>
                    </Row>
                  </Col>
                </Row>

                {planPeriodDetails && (
                  <Row className={'mb-8'}>
                    <Col span={24}>
                      <Row className="with_card">
                        <Col span={18} className="content">
                          <h5 className="mb-0">
                            <b>{t('admin-dashboard.benefit-details.restrict-reimbursement-categories-title')}</b>
                          </h5>
                          <h5 className="mb-0">
                            {planPeriodDetails.restrictedExpenseCategories.length > 0
                              ? planPeriodDetails.restrictedExpenseCategories.map((x) => x.name).join(',')
                              : 'N/A'}
                          </h5>
                        </Col>

                        <Col span={6} className="flex justify-end">
                          <Button
                            type="link"
                            className={'t-eligible-employees'}
                            disabled={!planPeriodDetails.isEditable}
                            onClick={() => {
                              history.push(
                                `${benefitDesignFullPath}${selectedPlanPeriod.id}/${BenefitDesignStep.BenefitRestrictReimbursementCategories}`,
                              )
                            }}
                          >
                            {t('admin-dashboard.benefit-details.summary.edit-button')}
                          </Button>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                )}

                <Row>
                  <Col span={24}>
                    <Card
                      title={<h5 className="mb-0">Benefit Design</h5>}
                      extra={
                        <Button
                          type="link"
                          className={'t-benefit-design'}
                          disabled={!planPeriodDetails.isEditable}
                          onClick={handleBenefitDesignEdit}
                        >
                          {t('admin-dashboard.benefit-details.summary.edit-button')}
                        </Button>
                      }
                    >
                      <Row>
                        <Col span={18}>
                          {planPeriodDetails && (
                            <ClassesSummary
                              classes={planPeriodDetails.productClasses.map((productClass) => ({
                                ...productClass,
                                totalEmployeesAssigned: productClass.totalEmployees,
                              }))}
                              productId={ProductIds.GroupHraId}
                              sameClassForAll={selectedPlanPeriod.sameClassForAll}
                              apiEndpoint={getProductClassDetails}
                            />
                          )}
                        </Col>
                      </Row>
                    </Card>
                  </Col>
                </Row>
              </>
            ) : (
              <SummaryEmpty planPeriodId={selectedPlanPeriod.id} />
            )}
          </>
        )}
      </Skeleton>
    </Card>
  )
}

export default GroupHraDesignBenefitSummary
